"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.cashExpenseDto = exports.createCashExpenseRequest = exports.treasuryTransferDto = exports.createTreasuryTransferRequest = exports.withholdingPayableReportResponse = exports.apAgingResponse = exports.supplierStatementResponse = exports.paymentListQuery = exports.supplierPaymentDto = exports.createSupplierPaymentRequest = exports.paymentWithholdingInput = exports.purchaseBillListQuery = exports.purchaseNoteListQuery = exports.purchaseNoteDto = exports.purchaseNoteSummary = exports.createPurchaseNoteRequest = exports.purchaseNoteLineInput = exports.purchaseNoteKind = exports.cancelPurchaseBillRequest = exports.purchaseBillDto = exports.purchaseBillCancellationDto = exports.purchaseBillLineDto = exports.purchaseBillSummary = exports.createPurchaseBillRequest = exports.purchaseBillLineInput = exports.createSupplierRequest = exports.supplierDto = void 0;
const zod_1 = require("zod");
const common_1 = require("./common");
const ledger_1 = require("./ledger");
const positiveMoney = common_1.moneyString.refine((value) => !value.startsWith('-') && /[1-9]/.test(value), 'amount must be positive');
const nonNegativeMoney = common_1.moneyString.refine((value) => !value.startsWith('-'), 'amount cannot be negative');
const quantityString = zod_1.z
    .string()
    .regex(/^\d{1,12}(\.\d{1,4})?$/, 'quantity must be a positive decimal string')
    .refine((value) => /[1-9]/.test(value), 'quantity must be positive');
const optionalText = (max) => zod_1.z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullish();
// ---------------------------------------------------------------------------
// الموردون
// ---------------------------------------------------------------------------
exports.supplierDto = zod_1.z.object({
    id: common_1.uuid,
    companyId: common_1.uuid,
    /** كود يولّده الخادم بالتتابع (S-000001). */
    code: zod_1.z.string(),
    nameAr: zod_1.z.string(),
    taxRegistrationNumber: zod_1.z.string().nullable(),
    phone: zod_1.z.string().nullable(),
    email: zod_1.z.string().nullable(),
    paymentTermsDays: zod_1.z.number().int(),
    isActive: zod_1.z.boolean(),
    createdAt: zod_1.z.string(),
});
exports.createSupplierRequest = zod_1.z
    .object({
    nameAr: zod_1.z.string().trim().min(2, 'name must be at least 2 characters').max(200),
    taxRegistrationNumber: zod_1.z
        .string()
        .trim()
        .regex(/^[0-9-]*$/, 'tax registration number may contain digits and dashes only')
        .max(32)
        .transform((value) => (value === '' ? null : value))
        .nullish(),
    phone: optionalText(32),
    email: zod_1.z
        .string()
        .trim()
        .max(254)
        .refine((value) => value === '' || zod_1.z.string().email().safeParse(value).success, 'invalid email')
        .transform((value) => (value === '' ? null : value))
        .nullish(),
    paymentTermsDays: zod_1.z.number().int().min(0).max(365).default(0),
})
    .strict();
// ---------------------------------------------------------------------------
// فواتير الموردين
// ---------------------------------------------------------------------------
exports.purchaseBillLineInput = zod_1.z
    .object({
    description: zod_1.z.string().trim().min(1).max(500),
    quantity: quantityString,
    unitPrice: nonNegativeMoney,
    /**
     * حساب المصروف أو الأصل المدين بهذا السطر — إلزامى بلا صنف: لا افتراض لطبيعة
     * المشتريات. مع صنف مخزنى يُدان حساب مخزون الصنف، فإن أُرسل وجب أن يساويه.
     */
    accountCode: common_1.accountCode.optional(),
    /** صنف مخزنى يدخل المخزون بهذا السطر — حين يكون المخزون مفعّلاً فى الإعدادات. */
    itemId: common_1.uuid.optional(),
    /** قاعدة ضريبية معتمدة. بلا قاعدة = بلا ضريبة مدخلات (BLOCKED-TAX). */
    taxRuleId: common_1.uuid.optional(),
})
    .strict()
    .superRefine((line, ctx) => {
    if (line.itemId === undefined && line.accountCode === undefined) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['accountCode'],
            message: 'account code is required without a stock item',
        });
    }
});
exports.createPurchaseBillRequest = zod_1.z
    .object({
    supplierId: common_1.uuid,
    billDate: common_1.isoDate,
    dueDate: common_1.isoDate.optional(),
    /** رقم الفاتورة كما أصدرها المورد — لا يتكرر لنفس المورد. */
    supplierReference: zod_1.z.string().trim().min(1).max(64).optional(),
    memo: zod_1.z.string().trim().max(500).optional(),
    lines: zod_1.z.array(exports.purchaseBillLineInput).min(1).max(200),
})
    .strict();
exports.purchaseBillSummary = zod_1.z.object({
    id: common_1.uuid,
    companyId: common_1.uuid,
    number: zod_1.z.string(),
    supplierReference: zod_1.z.string().nullable(),
    supplierId: common_1.uuid,
    supplierName: zod_1.z.string(),
    billDate: common_1.isoDate,
    dueDate: common_1.isoDate,
    currency: zod_1.z.string(),
    netTotal: common_1.moneyString,
    taxTotal: common_1.moneyString,
    total: common_1.moneyString,
    journalId: common_1.uuid,
    journalNumber: zod_1.z.string().nullable(),
    paid: common_1.moneyString,
    /** مجموع الإشعارات المدينة التى أصدرناها على الفاتورة (بالضريبة) — تخفض ذمة المورد. */
    debitNotesTotal: common_1.moneyString,
    /** مجموع الإشعارات الدائنة من المورد على الفاتورة (بالضريبة) — ترفع ذمته. */
    creditNotesTotal: common_1.moneyString,
    /** `cancelled`: أُلغيت بقيد عكسى — تبقى مستندًا منشورًا برقمها ولا يُسدد عليها. */
    status: zod_1.z.enum(['posted', 'cancelled']),
    /** المتبقى = الإجمالى + الدائنة − المدينة − المسدد، وصفر للملغاة. */
    openBalance: common_1.moneyString,
});
exports.purchaseBillLineDto = zod_1.z.object({
    lineNumber: zod_1.z.number().int(),
    description: zod_1.z.string(),
    quantity: zod_1.z.string(),
    unitPrice: common_1.moneyString,
    accountCode: common_1.accountCode,
    net: common_1.moneyString,
    taxAmount: common_1.moneyString,
    /** قاعدة السطر — يقترحها الإشعار على نفس السطر فتُعكس ضريبته بنفس الأثر. */
    taxRuleId: common_1.uuid.nullable(),
});
exports.purchaseBillCancellationDto = zod_1.z.object({
    cancellationDate: common_1.isoDate,
    reason: zod_1.z.string(),
    /** القيد العكسى — قيد الفاتورة الأصلى يبقى POSTED كما هو. */
    journalId: common_1.uuid,
    journalNumber: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string(),
});
exports.purchaseBillDto = exports.purchaseBillSummary.extend({
    memo: zod_1.z.string().nullable(),
    lines: zod_1.z.array(exports.purchaseBillLineDto),
    cancellation: exports.purchaseBillCancellationDto.nullable(),
});
/**
 * إلغاء فاتورة مورد بقيد عكسى كامل بتاريخ الإلغاء. يحكمه
 * `settings.purchases.invoiceCancellation`، ويُرفض لفاتورة عليها سداد أو إشعار، أو
 * بتاريخ فى فترة مقفلة.
 */
exports.cancelPurchaseBillRequest = zod_1.z
    .object({
    cancellationDate: common_1.isoDate,
    reason: zod_1.z.string().trim().min(3).max(500),
})
    .strict();
/** مدين نصدره للمورد يخفض ذمته (مرتجع/خصم)، ودائن منه يرفعها — كلاهما على فاتورة منشورة. */
exports.purchaseNoteKind = zod_1.z.enum(['debit', 'credit']);
exports.purchaseNoteLineInput = zod_1.z
    .object({
    description: zod_1.z.string().trim().min(1).max(500),
    quantity: quantityString,
    unitPrice: nonNegativeMoney,
    accountCode: common_1.accountCode,
    taxRuleId: common_1.uuid.optional(),
})
    .strict();
exports.createPurchaseNoteRequest = zod_1.z
    .object({
    kind: exports.purchaseNoteKind,
    billId: common_1.uuid,
    noteDate: common_1.isoDate,
    reason: zod_1.z.string().trim().min(3).max(500),
    /**
     * سطر بحساب المصروف أو الأصل وقاعدته الضريبية كسطر الفاتورة — بلا صنف مخزنى: مرتجع
     * المخزون يحتاج حركة صرف للمورد لم تُعتمد بعد (BLOCKED-ACCOUNTING)، وحسابات المخزون
     * رقابية فتُرفض هنا.
     */
    lines: zod_1.z.array(exports.purchaseNoteLineInput).min(1).max(200),
})
    .strict();
exports.purchaseNoteSummary = zod_1.z.object({
    id: common_1.uuid,
    companyId: common_1.uuid,
    kind: exports.purchaseNoteKind,
    number: zod_1.z.string(),
    billId: common_1.uuid,
    billNumber: zod_1.z.string(),
    supplierId: common_1.uuid,
    supplierName: zod_1.z.string(),
    noteDate: common_1.isoDate,
    reason: zod_1.z.string(),
    currency: zod_1.z.string(),
    netTotal: common_1.moneyString,
    taxTotal: common_1.moneyString,
    total: common_1.moneyString,
    journalId: common_1.uuid,
    journalNumber: zod_1.z.string().nullable(),
});
exports.purchaseNoteDto = exports.purchaseNoteSummary.extend({
    lines: zod_1.z.array(exports.purchaseBillLineDto),
});
exports.purchaseNoteListQuery = zod_1.z
    .object({
    billId: common_1.uuid.optional(),
    kind: exports.purchaseNoteKind.optional(),
})
    .strict();
exports.purchaseBillListQuery = zod_1.z
    .object({
    supplierId: common_1.uuid.optional(),
    openOnly: zod_1.z.enum(['true', 'false']).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// السداد للموردين
// ---------------------------------------------------------------------------
/**
 * الخصم من المنبع على السداد: مبلغ، أو نسبة على وعاء، أو الثلاثة متطابقة. النسبة يدخلها
 * المستخدم أو يختارها من `settings.tax.withholding.paymentRates` — لا نسبة فى الكود (`BLOCKED-TAX`).
 */
exports.paymentWithholdingInput = zod_1.z
    .object({
    amount: positiveMoney.optional(),
    ratePercent: positiveMoney.optional(),
    base: positiveMoney.optional(),
})
    .strict();
/**
 * سداد لمورد. التخصيصات حتى (النقد + الخصم من المنبع)، والباقى تحت الحساب. الخصم يحكمه
 * `settings.tax.withholding.onPayments`.
 */
exports.createSupplierPaymentRequest = zod_1.z
    .object({
    supplierId: common_1.uuid,
    paymentDate: common_1.isoDate,
    /** النقد المدفوع فعلاً. */
    amount: positiveMoney,
    /** حساب خزينة أو بنك أو محفظة طرفى — المدفوع منه. */
    sourceAccountCode: common_1.accountCode,
    externalReference: zod_1.z.string().trim().max(64).optional(),
    memo: zod_1.z.string().trim().max(500).optional(),
    allocations: zod_1.z
        .array(zod_1.z.object({ billId: common_1.uuid, amount: positiveMoney }).strict())
        .max(200)
        .default([]),
    withholding: exports.paymentWithholdingInput.optional(),
})
    .strict();
exports.supplierPaymentDto = zod_1.z.object({
    id: common_1.uuid,
    companyId: common_1.uuid,
    number: zod_1.z.string(),
    supplierId: common_1.uuid,
    supplierName: zod_1.z.string(),
    paymentDate: common_1.isoDate,
    amount: common_1.moneyString,
    sourceAccountCode: common_1.accountCode,
    externalReference: zod_1.z.string().nullable(),
    memo: zod_1.z.string().nullable(),
    journalId: common_1.uuid,
    journalNumber: zod_1.z.string().nullable(),
    /** ما خصمناه من المورد من المنبع — يسدد ذمته كالنقد، ونورّده للمصلحة. */
    withholdingAmount: common_1.moneyString,
    allocations: zod_1.z.array(zod_1.z.object({ billId: common_1.uuid, billNumber: zod_1.z.string(), amount: common_1.moneyString })),
    /** الباقى مدينًا على المورد تحت الحساب: (النقد + الخصم) − التخصيصات. */
    unallocated: common_1.moneyString,
});
exports.paymentListQuery = zod_1.z.object({ supplierId: common_1.uuid.optional() }).strict();
/** كشف حساب مورد: سطور حساب الموردين الحاملة لمعرّفه. الرصيد بطبيعة دائنة: الموجب مستحق له. */
exports.supplierStatementResponse = zod_1.z.object({
    companyId: common_1.uuid,
    supplier: zod_1.z.object({ id: common_1.uuid, code: zod_1.z.string(), nameAr: zod_1.z.string() }),
    accountCode: common_1.accountCode,
    from: common_1.isoDate,
    to: common_1.isoDate,
    openingBalance: common_1.moneyString,
    lines: zod_1.z.array(ledger_1.accountLedgerLine),
    totalDebit: common_1.moneyString,
    totalCredit: common_1.moneyString,
    closingBalance: common_1.moneyString,
});
const agingAmounts = {
    current: common_1.moneyString,
    days1to30: common_1.moneyString,
    days31to60: common_1.moneyString,
    days61to90: common_1.moneyString,
    over90: common_1.moneyString,
    /** سدادات لم تُخصص بعد — تُخصم من المستحق للمورد. */
    unappliedCredit: common_1.moneyString,
    total: common_1.moneyString,
};
/** أعمار المستحق للموردين مع المطابقة بحساب الموردين الرقابى (رصيد دائن موجب). */
exports.apAgingResponse = zod_1.z.object({
    companyId: common_1.uuid,
    asOf: common_1.isoDate,
    rows: zod_1.z.array(zod_1.z.object({ supplierId: common_1.uuid, code: zod_1.z.string(), nameAr: zod_1.z.string(), ...agingAmounts })),
    totals: zod_1.z.object(agingAmounts),
    controlAccountCode: common_1.accountCode,
    controlBalance: common_1.moneyString,
    unreconciled: common_1.moneyString,
});
/**
 * ضرائب الخصم المستحقة للتوريد فى فترة — ما خصمناه من الموردين ولم نورّده بعد. من سطور
 * حساب «ضرائب خصم مستحقة» فى الدفتر نفسه لا من جدول موازٍ: التوريد بقيد يدوى يظهر هنا.
 */
exports.withholdingPayableReportResponse = zod_1.z.object({
    companyId: common_1.uuid,
    accountCode: common_1.accountCode,
    from: common_1.isoDate,
    to: common_1.isoDate,
    /** رصيد الحساب قبل الفترة (دائن موجب = مستحق للمصلحة). */
    openingBalance: common_1.moneyString,
    bySupplier: zod_1.z.array(zod_1.z.object({
        supplierId: common_1.uuid.nullable(),
        code: zod_1.z.string().nullable(),
        nameAr: zod_1.z.string().nullable(),
        taxRegistrationNumber: zod_1.z.string().nullable(),
        debit: common_1.moneyString,
        credit: common_1.moneyString,
    })),
    lines: zod_1.z.array(zod_1.z.object({
        journalId: common_1.uuid,
        journalNumber: zod_1.z.string().nullable(),
        postingDate: common_1.isoDate,
        reference: zod_1.z.string().nullable(),
        supplierId: common_1.uuid.nullable(),
        supplierName: zod_1.z.string().nullable(),
        ratePercent: zod_1.z.string().nullable(),
        base: common_1.moneyString.nullable(),
        debit: common_1.moneyString,
        credit: common_1.moneyString,
    })),
    totalDebit: common_1.moneyString,
    totalCredit: common_1.moneyString,
    closingBalance: common_1.moneyString,
});
// ---------------------------------------------------------------------------
// الخزينة
// ---------------------------------------------------------------------------
exports.createTreasuryTransferRequest = zod_1.z
    .object({
    transferDate: common_1.isoDate,
    fromAccountCode: common_1.accountCode,
    toAccountCode: common_1.accountCode,
    amount: positiveMoney,
    externalReference: zod_1.z.string().trim().max(64).optional(),
    memo: zod_1.z.string().trim().max(500).optional(),
})
    .strict()
    .refine((value) => value.fromAccountCode !== value.toAccountCode, {
    message: 'source and destination accounts must differ',
    path: ['toAccountCode'],
});
exports.treasuryTransferDto = zod_1.z.object({
    id: common_1.uuid,
    companyId: common_1.uuid,
    number: zod_1.z.string(),
    transferDate: common_1.isoDate,
    fromAccountCode: common_1.accountCode,
    toAccountCode: common_1.accountCode,
    amount: common_1.moneyString,
    externalReference: zod_1.z.string().nullable(),
    memo: zod_1.z.string().nullable(),
    journalId: common_1.uuid,
    journalNumber: zod_1.z.string().nullable(),
});
// ---------------------------------------------------------------------------
// المصروف النقدى المباشر
// ---------------------------------------------------------------------------
/** مصروف دُفع لحظته من خزينة أو بنك، بلا فاتورة مورد ولا ذمة. */
exports.createCashExpenseRequest = zod_1.z
    .object({
    expenseDate: common_1.isoDate,
    sourceAccountCode: common_1.accountCode,
    payee: zod_1.z.string().trim().max(200).optional(),
    externalReference: zod_1.z.string().trim().max(64).optional(),
    memo: zod_1.z.string().trim().max(500).optional(),
    lines: zod_1.z
        .array(zod_1.z
        .object({
        description: zod_1.z.string().trim().min(1).max(500),
        amount: positiveMoney,
        /** حساب مصروف طرفى. */
        accountCode: common_1.accountCode,
    })
        .strict())
        .min(1)
        .max(100),
})
    .strict();
exports.cashExpenseDto = zod_1.z.object({
    id: common_1.uuid,
    companyId: common_1.uuid,
    number: zod_1.z.string(),
    expenseDate: common_1.isoDate,
    sourceAccountCode: common_1.accountCode,
    payee: zod_1.z.string().nullable(),
    externalReference: zod_1.z.string().nullable(),
    memo: zod_1.z.string().nullable(),
    total: common_1.moneyString,
    journalId: common_1.uuid,
    journalNumber: zod_1.z.string().nullable(),
    lines: zod_1.z.array(zod_1.z.object({ description: zod_1.z.string(), accountCode: common_1.accountCode, amount: common_1.moneyString })),
});
//# sourceMappingURL=payables.js.map